import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from './utils';

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  showSelectAll?: boolean;
}

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  ({ options, value = [], onValueChange, placeholder = 'Select items...', className, showSelectAll = true }, ref) => {
    const [open, setOpen] = React.useState(false);

    const handleSelect = (optionValue: string) => {
      const newValue = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
      onValueChange?.(newValue);
    };

    const handleRemove = (optionValue: string) => {
      const newValue = value.filter((v) => v !== optionValue);
      onValueChange?.(newValue);
    };

    const handleSelectAll = () => {
      const allSelected = options.length > 0 && value.length === options.length;
      const newValue = allSelected ? [] : options.map((option) => option.value);
      onValueChange?.(newValue);
    };

    const selectedOptions = options.filter((option) => value.includes(option.value));
    const allSelected = options.length > 0 && value.length === options.length;

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger
          ref={ref}
          className={cn(
            '!focus:ring-primary-500 flex  w-full items-center justify-between rounded-lg border border-[#CFD8DC] bg-white px-3 py-2 text-sm ring-offset-[#CFD8DC] placeholder:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}>
          <div className="flex flex-1 flex-wrap items-center gap-1">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <div key={option.value} className="flex items-center gap-1 rounded bg-primary-100 px-2 py-1 text-xs">
                  <span className="text-sh-xxs">{option.label}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(option.value);
                    }}
                    className="text-gray-500 hover:text-gray-700">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))
            ) : (
              <span className="text-sh-xxs text-gray-400">{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </PopoverPrimitive.Trigger>

        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            className="z-50 w-full min-w-[var(--radix-popover-trigger-width)] rounded-md border bg-white p-1 shadow-md"
            align="start">
            <div className="max-h-60 overflow-auto">
              {options.length > 0 && showSelectAll && (
                <>
                  <button
                    type="button"
                    className="flex w-full cursor-pointer items-center space-x-2 rounded-sm border-b border-gray-100 px-2 py-1.5 text-sm font-medium hover:bg-gray-100"
                    onClick={handleSelectAll}>
                    <div
                      className={`flex h-4 w-4 items-center justify-center rounded ${allSelected ? 'border-primary-500 bg-primary-500' : 'border'}`}>
                      {allSelected && <Check className="h-3 w-3" />}
                    </div>
                    <span className="text-sh-xxs">Select All</span>
                  </button>
                  <div className="my-1" />
                </>
              )}
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className="flex w-full cursor-pointer items-center space-x-2 rounded-sm px-2 py-1.5 text-sm hover:bg-gray-100"
                  onClick={() => handleSelect(option.value)}>
                  <div
                    className={`flex h-4 w-4 items-center justify-center rounded   ${value.includes(option.value) ? 'border-primary-500 bg-primary-500' : 'border'}`}>
                    {value.includes(option.value) && <Check className="h-3 w-3" />}
                  </div>
                  <span className="text-sh-xxs">{option.label}</span>
                </button>
              ))}
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';

export { MultiSelect };
export type { MultiSelectOption };
